import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { API_URL } from '../../config/api';
import { fixMessageLinkHtml } from '../../utils/fileUtils';
import { fetchUsers } from '../../store/slices/userSlice';
import {
  extractMentionUsernames,
  findMentionTriggerIndex,
  findUserByMention,
} from '../../utils/mentionDisplayUtils';
import PresignedAvatar from '../common/PresignedAvatar';
import 'react-quill/dist/quill.snow.css';

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const quillFormats = [
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link',
];

const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

const hasMeaningfulContent = (htmlContent, textContent) => {
  if (textContent && textContent.trim().length > 0) {
    return true;
  }
  if (!htmlContent || htmlContent === '<p><br></p>' || htmlContent === '<p></p>') {
    return false;
  }
  return stripHtmlTags(htmlContent).length > 0;
};

const CallNotesInput = ({
  meetingMessageId,
  autoFocus = false,
  onNotePosted,
  compact = false,
}) => {
  const dispatch = useDispatch();
  const { users = [] } = useSelector((state) => state.users);
  const { user: currentUser } = useSelector((state) => state.auth);

  const [content, setContent] = useState('');
  const [hasContent, setHasContent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);

  const quillRef = useRef(null);
  const mentionDropdownRef = useRef(null);
  const mentionedUserIdsRef = useRef(new Set());

  const collectMentionedUserIds = useCallback((plainText) => {
    const ids = new Set(mentionedUserIdsRef.current);
    extractMentionUsernames(plainText).forEach((token) => {
      const user = findUserByMention(users, token);
      if (user?.id && user.id !== currentUser?.id) {
        ids.add(user.id);
      }
    });
    return Array.from(ids);
  }, [users, currentUser?.id]);

  const detectMention = useCallback((editor) => {
    if (!editor) return;

    if (users.length === 0) {
      setShowMentionSuggestions(false);
      return;
    }

    setTimeout(() => {
      const selection = editor.getSelection();
      if (!selection) {
        setShowMentionSuggestions(false);
        return;
      }

      const text = editor.getText();
      const cursorIndex = selection.index;
      const trigger = findMentionTriggerIndex(text, cursorIndex);
      if (trigger.type !== 'user' || trigger.index === -1) {
        setShowMentionSuggestions(false);
        return;
      }

      const atIndex = trigger.index;
      const query = text.substring(atIndex + 1, cursorIndex).toLowerCase();
      const filteredUsers = users
        .filter((u) => u.id !== currentUser?.id)
        .filter((u) => {
          if (!query) return true;
          const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
          const username = (u.username || '').toLowerCase();
          return fullName.includes(query) || username.includes(query);
        })
        .slice(0, 10);

      if (filteredUsers.length > 0) {
        setMentionSuggestions(filteredUsers);
        setMentionStartIndex(atIndex);
        setSelectedMentionIndex(0);
        setShowMentionSuggestions(true);
      } else {
        setShowMentionSuggestions(false);
      }
    }, 10);
  }, [users, currentUser?.id]);

  const insertMention = useCallback((user) => {
    const editor = quillRef.current?.getEditor();
    if (!editor || mentionStartIndex === -1) return;

    const selection = editor.getSelection();
    if (!selection) return;

    const cursorIndex = selection.index;
    const queryLength = cursorIndex - mentionStartIndex;
    editor.deleteText(mentionStartIndex, queryLength);

    const mentionText = `@${user.username} `;
    editor.insertText(mentionStartIndex, mentionText);
    editor.setSelection(mentionStartIndex + mentionText.length);

    setShowMentionSuggestions(false);
    setMentionStartIndex(-1);

    if (user?.id) {
      mentionedUserIdsRef.current.add(user.id);
    }

    editor.root.focus();
  }, [mentionStartIndex]);

  useEffect(() => {
    if (users.length === 0) {
      dispatch(fetchUsers());
    }
  }, [dispatch, users.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mentionDropdownRef.current
        && !mentionDropdownRef.current.contains(event.target)
        && quillRef.current
        && !quillRef.current.getEditor()?.root.contains(event.target)
      ) {
        setShowMentionSuggestions(false);
      }
    };

    if (showMentionSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }

    return undefined;
  }, [showMentionSuggestions]);

  const handleQuillChange = (value, _delta, source) => {
    setContent(value);
    const quillEditor = quillRef.current?.getEditor();
    const textContent = quillEditor?.getText() || '';
    setHasContent(hasMeaningfulContent(value, textContent));

    if (source !== 'api' && quillEditor) {
      detectMention(quillEditor);
    }
  };

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();

    const quillEditor = quillRef.current?.getEditor();
    const textContent = quillEditor?.getText() || '';
    const htmlContent = quillEditor?.root.innerHTML || '';

    if (!hasMeaningfulContent(htmlContent, textContent) || !meetingMessageId || loading) {
      return;
    }

    const normalizedHtml = htmlContent !== '<p><br></p>' && htmlContent.trim()
      ? fixMessageLinkHtml(htmlContent)
      : htmlContent;
    const contentToSend = normalizedHtml !== '<p><br></p>' && normalizedHtml.trim()
      ? normalizedHtml
      : textContent.trim();
    const mentionedUserIds = collectMentionedUserIds(textContent);

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/messages/${meetingMessageId}/meeting-note`,
        { content: contentToSend, mentionedUserIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (quillEditor) {
        quillEditor.setText('');
      }
      setContent('');
      setHasContent(false);
      setShowMentionSuggestions(false);
      mentionedUserIdsRef.current.clear();
      onNotePosted?.(response.data);
    } catch (err) {
      const msg = err.response?.data;
      setError(typeof msg === 'string' ? msg.replace(/^Error:\s*/i, '') : 'Failed to post note');
    } finally {
      setLoading(false);
    }
  }, [collectMentionedUserIds, loading, meetingMessageId, onNotePosted]);

  useEffect(() => {
    if (!autoFocus) return undefined;

    const timeoutId = setTimeout(() => {
      quillRef.current?.getEditor()?.focus();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [autoFocus]);

  useEffect(() => {
    let timeoutId = null;
    let editorRoot = null;
    let keyDownHandler = null;

    const setupKeyHandler = () => {
      const quillEditor = quillRef.current?.getEditor();
      if (!quillEditor) {
        timeoutId = setTimeout(setupKeyHandler, 100);
        return;
      }

      editorRoot = quillEditor.root;
      keyDownHandler = (e) => {
        if (showMentionSuggestions && mentionSuggestions.length > 0) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedMentionIndex((prev) => (
              prev < mentionSuggestions.length - 1 ? prev + 1 : prev
            ));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0));
            return;
          }
          if (e.key === 'Enter' || e.keyCode === 13) {
            e.preventDefault();
            e.stopPropagation();
            insertMention(mentionSuggestions[selectedMentionIndex]);
            return;
          }
          if (e.key === 'Escape' || e.keyCode === 27) {
            e.preventDefault();
            setShowMentionSuggestions(false);
            return;
          }
        }

        if (e.key === '@' || (e.key === '2' && e.shiftKey)) {
          setTimeout(() => detectMention(quillEditor), 50);
          return;
        }

        const isEnterOrReturn = e.key === 'Enter' || e.keyCode === 13;
        if (!isEnterOrReturn) return;

        if (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const currentTextContent = quillEditor.getText() || '';
        const currentHtmlContent = quillEditor.root.innerHTML || '';
        if (hasMeaningfulContent(currentHtmlContent, currentTextContent)) {
          handleSubmit(e);
        }
      };

      editorRoot.addEventListener('keydown', keyDownHandler);
    };

    setupKeyHandler();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (editorRoot && keyDownHandler) {
        editorRoot.removeEventListener('keydown', keyDownHandler);
      }
    };
  }, [
    detectMention,
    handleSubmit,
    insertMention,
    mentionSuggestions,
    selectedMentionIndex,
    showMentionSuggestions,
  ]);

  const editorMinHeight = compact ? 16 : 20;
  const editorMaxHeight = compact ? 120 : 180;

  return (
    <form onSubmit={handleSubmit} className={compact ? 'space-y-1.5' : 'space-y-2'}>
      <div className="relative border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 overflow-visible">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={content}
          onChange={handleQuillChange}
          modules={quillModules}
          formats={quillFormats}
          placeholder="Type a call note…"
          className="bg-transparent call-notes-quill"
          style={{
            minHeight: `${editorMinHeight}px`,
            maxHeight: `${editorMaxHeight}px`,
            marginBottom: '42px',
          }}
        />

        {showMentionSuggestions && mentionSuggestions.length > 0 && (
          <div
            ref={mentionDropdownRef}
            className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
          >
            {mentionSuggestions.map((user, index) => (
              <button
                key={user.id}
                type="button"
                onClick={() => insertMention(user)}
                className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  index === selectedMentionIndex
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : ''
                }`}
              >
                <PresignedAvatar
                  fileKey={user.profilePicture}
                  name={`${user.firstName || ''} ${user.lastName || ''}`}
                  initials={`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  fallbackClassName="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    @{user.username}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        <style>{`
          .call-notes-quill .ql-container {
            font-size: 14px;
            min-height: ${editorMinHeight}px;
            max-height: ${editorMaxHeight}px;
            overflow-y: auto !important;
            overflow-x: hidden;
            border: none !important;
          }
          .call-notes-quill .ql-editor {
            min-height: ${editorMinHeight}px;
            color: #111827;
            overflow-x: hidden;
            word-wrap: break-word;
            word-break: break-word;
            padding: 10px 12px !important;
          }
          .call-notes-quill .ql-editor:focus {
            outline: none;
          }
          .dark .call-notes-quill .ql-editor {
            color: #f9fafb;
          }
          .call-notes-quill .ql-toolbar {
            border-bottom: 1px solid #e5e7eb;
            border-left: none;
            border-right: none;
            border-top: none;
            overflow-x: hidden !important;
            white-space: normal !important;
            display: flex !important;
            flex-wrap: wrap !important;
            padding: 6px 8px;
          }
          .call-notes-quill .ql-toolbar .ql-formats {
            display: inline-flex !important;
            flex-wrap: wrap !important;
          }
          .dark .call-notes-quill .ql-toolbar {
            background: #374151;
            border-bottom-color: #4b5563;
          }
          .dark .call-notes-quill .ql-stroke {
            stroke: #d1d5db;
          }
          .dark .call-notes-quill .ql-fill {
            fill: #d1d5db;
          }
          .dark .call-notes-quill .ql-picker-label {
            color: #d1d5db;
          }
        `}</style>
      </div>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] text-gray-500 dark:text-gray-400">
          @ to mention · Enter to post · Shift + Enter for new line
        </p>
        <button
          type="submit"
          disabled={loading || !hasContent}
          className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg"
        >
          {loading ? 'Posting…' : 'Post note'}
        </button>
      </div>
    </form>
  );
};

export default CallNotesInput;
